import React from "react";
import { 
  Col, 
  Row, 
  Button, 
  Input,
} from "reactstrap";

import ToolkitProvider, {
  CSVExport
} from "react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import StatementUnavailbleTooltip from "./statementUnavailableTooltip";
import { sendAuditEvent } from "../utils/auditTrail";
import { useTranslation } from "react-i18next";

const StatementTable = ({ tableData, searchQuery, defaultSorted, auditData }) => {  

  const { ExportCSVButton } = CSVExport;

  const { t } = useTranslation();

  const months = [
    t("statements.view.table.label.month_january"),
    t("statements.view.table.label.month_february"),
    t("statements.view.table.label.month_march"),
    t("statements.view.table.label.month_april"),
    t("statements.view.table.label.month_may"),
    t("statements.view.table.label.month_june"),
    t("statements.view.table.label.month_july"),
    t("statements.view.table.label.month_august"),
    t("statements.view.table.label.month_september"),
    t("statements.view.table.label.month_october"),
    t("statements.view.table.label.month_november"),
    t("statements.view.table.label.month_december")
  ];

  const currencies = ["€", "$", "£"];

  const monthFormatter = (cell, row) => {
    cell = months[cell - 1];
    return cell;
  };

  const amountFormatter = (cell, row) => `${cell}${currencies[row.currency]}`;

  const statusFormatter = cell =>
    cell === 0 ? (
      <Button className="btn-status" disabled color="warning">
        {t("statements.view.table.label.status_open")}
      </Button>
    ) : cell === 1 ? (
      <Button className="btn-status" disabled color="success">
        {t("statements.view.table.label.status_paid")}
      </Button>
    ) : null;

  const typeFormatter = cell =>
    cell === 1
      ? (cell = t("statements.view.table.label.type_licensing"))
      : cell === 2
      ? (cell = t("statements.view.table.label.type_regular"))
      : cell === 3
      ? (cell = t("statements.view.table.label.type_audio_mastering_services"))
      : cell === 4
      ? (cell = t("statements.view.table.label.type_vinyl_earnings"))
      : cell === 5
      ? (cell = t("statements.view.table.label.type_offset"))
      : cell === 6
      ? (cell = t("statements.view.table.label.type_escrow_refund"))
      : (cell = t("statements.view.table.label.type_not_known"));

  const downloadFormatter = (cell, row) =>
    cell ?
    (
      <a
        href={cell}
        target="blank"
        rel="noreferrer"
        onClick={() => {
          sendAuditEvent({
            token: auditData?.token,
            eventType: 3,
            route: auditData?.route || window.location.pathname,
            entityType: "statement",
            entityIds: [row.id],
          });
        }}
      >
        <Button className="btn-status" color="primary">
          <b>{t("statements.view.table.button.download")}</b>
        </Button>
      </a>
    ) :
    <center>
      <StatementUnavailbleTooltip id={row.id} />
    </center>;

  const columns = [
    {
      dataField: "sts_kind",
      text: t("statements.view.table.column.type"),
      sort: true,
      formatter: typeFormatter,
      csvFormatter: typeFormatter,
      headerStyle: (colum, colIndex) => {
        return { width: "80px" };
      }
    },
    {
      dataField: "year",
      text: t("statements.view.table.column.year"),
      sort: true,
      headerStyle: (colum, colIndex) => {
        return { width: "80px" };
      }
    },
    {
      dataField: "month",
      text: t("statements.view.table.column.month"),
      sort: true,
      formatter: monthFormatter,
      csvFormatter: cell => months[cell - 1],
      headerStyle: (colum, colIndex) => {
        return { width: "80px" };
      }
    },
    {
      dataField: "issue_year",
      text: t("statements.view.table.column.issue_year"),
      sort: true,
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },
    {
      dataField: "issue_month",
      text: t("statements.view.table.column.issue_month"),
      sort: true,
      formatter: monthFormatter,
      csvFormatter: cell => months[cell - 1],
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },
    {
      dataField: "short_description",
      text: t("statements.view.table.column.description"),
      headerStyle: (colum, colIndex) => {
        return { width: "150px" };
      }
    },
    {
      dataField: "price",
      text: t("statements.view.table.column.amount"),
      sort: true,
      formatter: amountFormatter,
      csvFormatter: amountFormatter,
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },
    {
      dataField: "status",
      text: t("statements.view.table.column.status"),
      sort: true,
      formatter: statusFormatter,
      csvFormatter: cell =>
        cell === 0 ? "Open" : cell === 1 ? "  Paid" : null,
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },
    {
      dataField: "new_details",
      text: t("statements.view.table.column.details"),
      headerAlign: "center",
      align: "center",
      formatter: (cell, row) => downloadFormatter(cell, row),
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },
    {
      dataField: "new_overview",
      text: t("statements.view.table.column.overview"),
      headerAlign: "center",
      align: "center",
      formatter: (cell, row) => downloadFormatter(cell, row),
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },

    //data for search
    {
      dataField: "status",
      sort: false,
      csvExport: false,
      hidden: true,
      formatter: cell => (cell === 0 ? "Open" : cell === 1 ? "Paid" : null)
    }
  ];

  const handleTableChange = (type, { sortField, sortOrder, data }) => {
    const ascDesc = sortOrder === "desc" ? "-" : "";
    if (sortField === "kind" || sortField === "sts_kind" || sortField === "quartal" || sortField === "month" || sortField === "year" || sortField === "issue_month" || sortField === "issue_year" || sortField === "price") {
      searchQuery.setOrdering({order: ascDesc, name: sortField, sort: sortField});
    }
    if (sortField === "name") {
      searchQuery.setOrdering({order: ascDesc, name: "id", sort: sortField});
    }
  }


  return (
    <div>
      <ToolkitProvider
        keyField="id"
        data={tableData}
        columns={columns}
        search={{
          searchFormatted: true
        }}
        exportCSV={ {
          fileName: 'statements.csv',
        } }
      >
        {props => (
          <div>
            <Row>
              <Col>
                <div className="customSearch">
                  <Input placeholder={t("statements.view.table.placeholder.search")} value={searchQuery.query} style={{marginBottom: 10}} onChange={e => searchQuery.setQuery(e.target.value)}/>
                </div>
              </Col>
              <Col>
                <div className="flexEndContainer">
                                 
                  <ExportCSVButton
                    className="btn btn-outline-info"
                    {...props.csvProps}
                  >
                    {t("statements.view.table.button.exportCsv")}
                  </ExportCSVButton>
                </div>
              </Col>
            </Row>
            <BootstrapTable
              {...props.baseProps}
              bordered={ false }
              remote={ { sort: true } }
              defaultSorted={defaultSorted}
              onTableChange={handleTableChange}
            />
          </div>
        )}
      </ToolkitProvider>
    </div>
  );
};

export default StatementTable;
